import productsItemsApi from '_api/product'
import { DEFAULT_KEY, generateCacheTTL } from 'redux-cache'

const GET_LIST_PRODUCTS_BY_HOST = 'GET_LIST_PRODUCTS_BY_HOST'
const SEARCH_PRODUCTS_BY_HOST = 'SEARCH_PRODUCTS_BY_HOST'
const UPDATE_PRODUCT = 'UPDATE_PRODUCT'
const ADD_PRODUCT_TO_EVENT = 'ADD_PRODUCT_TO_EVENT'
const DELETE_PRODUCT = 'DELETE_PRODUCT'
const DELETE_MULTIPLE_PRODUCTS = 'DELETE_MULTIPLE_PRODUCTS'
const ASSIGN_MULTIPLE_PRODUCTS_CATEGORY = 'ASSIGN_MULTIPLE_PRODUCTS_CATEGORY'

const initialState = {
  [DEFAULT_KEY]: null,
  listProduct: [],
  searchResult: {
    keyword: '',
    events: [],
    lastProductTimestamp: null,
    lastProductId: null,
    hasMore: false,
  },
}

export const assignMultipleProductsCategoryAction =
  (productIds, categoryId, categoryName) => async (dispatch) => {
    try {
      const response = await productsItemsApi.assignMultipleProductsCategory({
        productIds,
        categoryId,
      })
      dispatch({
        type: ASSIGN_MULTIPLE_PRODUCTS_CATEGORY,
        payload: { productIds, categoryId, categoryName },
      })
      return response
    } catch (error) {
      console.error('assignMultipleProductsCategoryAction error:', error)
      throw error
    }
  }

export const unassignProductsCategoryAction = (productIds) => (dispatch) => {
  dispatch({
    type: ASSIGN_MULTIPLE_PRODUCTS_CATEGORY,
    payload: { productIds, categoryId: null, categoryName: '' },
  })
}

export const assignProductCategoryAction =
  (productId, eventId, categoryId, categoryName) => async (dispatch) => {
    try {
      const response = await productsItemsApi.assignProducCategory({ productId, categoryId })
      dispatch({
        type: UPDATE_PRODUCT,
        payload: { id: productId, eventId, data: { categoryId, categoryName } },
      })
      return response
    } catch (error) {
      console.error('assignProductCategoryAction error:', error)
      throw error
    }
  }

const mergeEvents = (existingEvents, incomingEvents) => {
  const eventsById = existingEvents.reduce((acc, event) => {
    acc[event.id] = { ...event, products: [...(event.products || [])] }
    return acc
  }, {});

  (incomingEvents || []).forEach((event) => {
    if (eventsById[event.id]) {
      eventsById[event.id].products = [...eventsById[event.id].products, ...(event.products || [])]
    } else {
      eventsById[event.id] = event
    }
  })

  return Object.values(eventsById)
}

const updateProductsInEvents = (events = [], updateFn) =>
  events.map((event) => ({
    ...event,
    products: (event.products || []).map(updateFn),
  }))

export const getListProductsByHost = (params) => async (dispatch) => {
  try {
    const { msgResp } = await productsItemsApi.getListProductsByHost(params)
    dispatch({
      type: GET_LIST_PRODUCTS_BY_HOST,
      payload: {
        listProduct: msgResp,
        append: !!(params?.lastProductTimestamp && params?.lastProductId),
      },
    })
  } catch (error) {
    console.error('getListProductsByHost error:', error)
  }
}

export const searchProductsByHostAction = (params) => async (dispatch) => {
  try {
    const { msgResp } = await productsItemsApi.searchProductsByHost(params)
    dispatch({
      type: SEARCH_PRODUCTS_BY_HOST,
      payload: {
        keyword: (params?.searchString || '').trim().toLowerCase(),
        searchResult: msgResp,
        append: !!(params?.lastProductTimestamp && params?.lastProductId),
      },
    })
  } catch (error) {
    console.error('searchProductsByHost error:', error)
  }
}

export const editProductAction = (id, eventId, data) => async (dispatch) => {
  try {
    const response = await productsItemsApi.editProduct(id, data)
    dispatch({
      type: UPDATE_PRODUCT,
      payload: { id, eventId, data },
    })
    return response
  } catch (error) {
    console.error('editProductAction error:', error)
    throw error
  }
}

export const addProductToEventAction = (eventId, data) => async (dispatch) => {
  try {
    const response = await productsItemsApi.addProductToEvent(eventId, data)
    dispatch({
      type: ADD_PRODUCT_TO_EVENT,
      payload: { eventId, data: response.msgResp },
    })
    return response
  } catch (error) {
    console.error('addProductToEventAction error:', error)
    throw error
  }
}

export const deleteProductAction = (productId, eventId) => async (dispatch) => {
  try {
    const response = await productsItemsApi.deleteProduct(productId, eventId)
    dispatch({
      type: DELETE_PRODUCT,
      payload: { productId, eventId },
    })
    return response
  } catch (error) {
    console.error('deleteProductAction error:', error)
    throw error
  }
}

export const deleteMultipleProductsAction = (data) => async (dispatch) => {
  try {
    const response = await productsItemsApi.deleteMultipleProducts(data)
    dispatch({
      type: DELETE_MULTIPLE_PRODUCTS,
      payload: { data },
    })
    return response
  } catch (error) {
    console.error('deleteMultipleProductsAction error:', error)
    throw error
  }
}

const productReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_LIST_PRODUCTS_BY_HOST: {
      const { listProduct, append } = action.payload

      if (!append || !state.listProduct?.events) {
        return {
          ...state,
          [DEFAULT_KEY]: generateCacheTTL(),
          listProduct,
        }
      }

      return {
        ...state,
        [DEFAULT_KEY]: generateCacheTTL(),
        listProduct: {
          ...listProduct,
          events: mergeEvents(state.listProduct.events, listProduct.events),
        },
      }
    }
    case SEARCH_PRODUCTS_BY_HOST: {
      const { searchResult, append, keyword } = action.payload
      const isSameKeyword = state.searchResult?.keyword === keyword

      if (!append || !isSameKeyword || !state.searchResult?.events?.length) {
        return {
          ...state,
          searchResult: { ...searchResult, keyword, events: searchResult.events || [] },
        }
      }

      return {
        ...state,
        searchResult: {
          ...searchResult,
          keyword,
          events: mergeEvents(state.searchResult.events, searchResult.events),
        },
      }
    }
    case UPDATE_PRODUCT: {
      const { id, eventId, data } = action.payload

      if (!state.listProduct || !state.listProduct.events) {
        return state
      }

      const newEvents = state.listProduct.events.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            products: (event.products || []).map((p) => (p.id === id ? { ...p, ...data } : p)),
          }
        }
        return event
      })

      return {
        ...state,
        listProduct: {
          ...state.listProduct,
          events: newEvents,
        },
      }
    }
    case ADD_PRODUCT_TO_EVENT: {
      const { eventId, data } = action.payload

      if (!state.listProduct || !state.listProduct.events) {
        return state
      }

      const newEvents = state.listProduct.events.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            products: [...(event.products || []), data],
          }
        }
        return event
      })

      return {
        ...state,
        listProduct: {
          ...state.listProduct,
          events: newEvents,
        },
      }
    }
    case DELETE_PRODUCT: {
      const { productId, eventId } = action.payload

      if (!state.listProduct || !state.listProduct.events) {
        return state
      }

      const newEvents = state.listProduct.events.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            products: (event.products || []).filter((p) => p.id !== productId),
          }
        }
        return event
      })

      return {
        ...state,
        listProduct: {
          ...state.listProduct,
          events: newEvents,
        },
      }
    }
    case DELETE_MULTIPLE_PRODUCTS: {
      const { data } = action.payload

      if (!state.listProduct || !state.listProduct.events) {
        return state
      }

      const newEvents = state.listProduct.events.map((event) => {
        const deletedProductIds = (data.products || [])
          .filter((p) => p.eventId === event.id)
          .map((p) => p.productId)

        if (deletedProductIds.length > 0) {
          return {
            ...event,
            products: (event.products || []).filter((p) => !deletedProductIds.includes(p.id)),
          }
        }
        return event
      })

      return {
        ...state,
        listProduct: {
          ...state.listProduct,
          events: newEvents,
        },
      }
    }
    case ASSIGN_MULTIPLE_PRODUCTS_CATEGORY: {
      const { productIds, categoryId, categoryName } = action.payload
      if (!Array.isArray(productIds) || productIds.length === 0) return state

      const updateFn = (p) => (productIds.includes(p.id) ? { ...p, categoryId, categoryName } : p)

      return {
        ...state,
        listProduct: state.listProduct?.events
          ? {
              ...state.listProduct,
              events: updateProductsInEvents(state.listProduct.events, updateFn),
            }
          : state.listProduct,
        searchResult: state.searchResult?.events?.length
          ? {
              ...state.searchResult,
              events: updateProductsInEvents(state.searchResult.events, updateFn),
            }
          : state.searchResult,
      }
    }
    default:
      return state
  }
}

export default productReducer
